import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import React, { ReactElement } from 'react';
import ProductManager from '../services/api/ProductManager';
import { ProductInterface } from '../services/exports/Interfaces';
import useStore from '../hooks/store/useStore';
import { REDUX_STORE_SLICES } from '../services/exports/Constants';
import collect from 'collect.js';
import { useSelector } from 'react-redux';
import { selectCompanyId } from '../store/slices/InitialDataSlice';
import { companiesApiSlice } from '../store/api-slices';
import useGetCompanyMenusApi from '../hooks/api/companies/useGetCompanyMenusApi';

interface Props {
  children: ReactElement[];
}

export interface ProductLikesContextInterface {
  likedProductIds: number[];
  likeProduct: (id: number) => void;
  unlikeProduct: (id: number) => void;
  toggleProductLike: (id: number) => void;
  isLiked: (id: number) => boolean;
}

export const ProductLikesContext = React.createContext<ProductLikesContextInterface>(null);

export default function ProductLikesProvider({ children }: Props) {
  const { likedProductIds } = useStore<REDUX_STORE_SLICES.PRODUCT_LIKES>(REDUX_STORE_SLICES.PRODUCT_LIKES);
  const companyId = useSelector(selectCompanyId);
  const { data: menusData } = useGetCompanyMenusApi();
  const getProduct = (id: number): ProductInterface => {
    return collect(menusData?.products).get(id) as ProductInterface;
  };

  const likeProduct = (id: number): void => {
    new Promise((resolve) => {
      const product = getProduct(id);
      if (!product) return;
      ReduxActions.batchActions([
        actionCreators.productLikes.likeProduct(id),
        companiesApiSlice.util.updateQueryData('getCompanyMenusData', companyId, (draft) => {
          draft.products[id].likes_count += 1;
        }),
      ]);
      resolve(true);
    }).then(() => ProductManager.likeProduct(id));
  };

  const unlikeProduct = (id: number): void => {
    new Promise((resolve) => {
      const product = getProduct(id);
      if (!product) return;
      ReduxActions.batchActions([
        actionCreators.productLikes.unlikeProduct(id),
        companiesApiSlice.util.updateQueryData('getCompanyMenusData', companyId, (draft) => {
          draft.products[id].likes_count = draft.products[id].likes_count > 0 ? product.likes_count - 1 : 0;
        }),
      ]);
      resolve(true);
    }).then(() => ProductManager.unlikeProduct(id));
  };

  const isLiked = (id: number): boolean => {
    return likedProductIds.includes(id);
  };

  const toggleProductLike = (id: number): void => {
    if (isLiked(id)) {
      return unlikeProduct(id);
    }

    likeProduct(id);
  };

  const context: ProductLikesContextInterface = {
    likedProductIds,
    isLiked,
    likeProduct,
    unlikeProduct,
    toggleProductLike,
  };

  return <ProductLikesContext.Provider value={context}>{children}</ProductLikesContext.Provider>;
}
